// src/components/ClientDetailsSidebar.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";

const statusStyles = {
  Active: { bgcolor: "#8CD47E", color: "#ffffff" },
  APPROVED: { bgcolor: "#8CD47E", color: "#ffffff" },
  Pending: { bgcolor: "#F8D66D", color: "#ffffff" },
  PENDING: { bgcolor: "#F8D66D", color: "#ffffff" },
  Rejected: { bgcolor: "#FF6961", color: "#ffffff" },
  REJECTED: { bgcolor: "#FF6961", color: "#ffffff" },
  UNASSIGNED: { bgcolor: "#F8D66D", color: "#fff" },
  ASSIGNED: { bgcolor: "#FFB54C", color: "#fff" },
  VISITED: { bgcolor: "#B3EBF2", color: "#fff" },
  SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
  FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
  REJECTED: { bgcolor: "#FF6961", color: "#fff" },
};

// Function to calculate remaining credits
const calculateRemainingCredits = (client) => {
  const used = client?.credits?.usedCredits || 0;
  const remaining = client?.credits?.totalCredits || 0;
  const total = used + remaining;

  const outstationUsed = client?.credits?.outstationCredits?.usedCredits || 0;
  const outstationRemaining =
    client?.credits?.outstationCredits?.totalCredits || 0;
  const outstationTotal = outstationUsed + outstationRemaining;

  return {
    normal: {
      remaining: remaining,
      total: total,
      used: used,
      display: total === 0 && used === 0 ? "-" : `${remaining}/${total}`,
    },
    outstation: {
      remaining: outstationRemaining,
      total: outstationTotal,
      used: outstationUsed,
      display:
        outstationTotal === 0 && outstationUsed === 0
          ? "-"
          : `${outstationRemaining}/${outstationTotal}`,
    },
  };
};

// Function to format status display
const getStatusText = (status) => {
  switch (status) {
    case "APPROVED":
      return "Active";
    case "PENDING":
      return "Pending";
    case "REJECTED":
      return "Rejected";
    default:
      return status || "Pending";
  }
};

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "N/A";
  const dateObj = new Date(date);
  return dateObj
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
    .replace(/\//g, "/");
};

const ClientDetailsSidebar = ({ open, onClose, client }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && client?._id) {
      fetchClientProjects();
    }
  }, [open, client?._id]);

  const fetchClientProjects = async () => {
    if (!client?._id || !token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${url}/api/client/requests`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          clientID: client._id,
          limit: 100, // Fetch all projects for this client
        },
      });

      const allRequests = response.data?.requests || [];

      // Transform project data
      const transformedProjects = allRequests.map((item) => {
        // Format Request Date
        let reqDateDisplay = "N/A";
        if (item.preferredSiteVisitDate?.fromDate) {
          const fromDate = new Date(item.preferredSiteVisitDate.fromDate);
          const toDate = item.preferredSiteVisitDate.toDate
            ? new Date(item.preferredSiteVisitDate.toDate)
            : null;

          if (!isNaN(fromDate)) {
            const fromFormatted = formatDate(fromDate);
            if (toDate && !isNaN(toDate)) {
              const toFormatted = formatDate(toDate);
              reqDateDisplay =
                fromDate.toDateString() === toDate.toDateString()
                  ? fromFormatted
                  : `${fromFormatted} - ${toFormatted}`;
            } else {
              reqDateDisplay = fromFormatted;
            }
          }
        } else if (item.requestedDate) {
          reqDateDisplay = formatDate(item.requestedDate);
        }

        // Format VISIT date - FIXED: Use assignedVisitDate to match AllProjects.js
        let visitedDateDisplay = "Not Scheduled";
        if (item.assignedVisitDate) {
          try {
            let date = null;
            if (item.assignedVisitDate.$date) {
              date = new Date(item.assignedVisitDate.$date);
            } else if (
              item.assignedVisitDate instanceof Date ||
              typeof item.assignedVisitDate === "string"
            ) {
              date = new Date(item.assignedVisitDate);
            }

            if (date && !isNaN(date.getTime())) {
              visitedDateDisplay = formatDate(date);
            }
          } catch (error) {
            console.error("Error formatting visited date:", error);
          }
        }

        // Get engineer details
        let engineerName = "Not Assigned";
        let engineerContact = "";

        if (item.assignedEngineer) {
          if (typeof item.assignedEngineer === "object") {
            engineerName = item.assignedEngineer.name || "Not Assigned";
            engineerContact = item.assignedEngineer.phone || "";
          } else {
            engineerName = "Not Assigned";
          }
        }

        // Get project report number
        const projectReportNo = item.projectReportNo || "—";

        return {
          id: item._id || item.id,
          projectReportNo: projectReportNo,
          projectName: item.nameOfProject || "N/A",
          siteCheck: item.templateTitle || item.templateID?.title || "N/A",
          reqDate: reqDateDisplay,
          engineer: engineerName,
          engineerContact: engineerContact,
          assignedVisitDate: visitedDateDisplay,
          status: item.status || "UNKNOWN",
          originalStatus: item.status || "UNKNOWN",
          _id: item._id,
        };
      });

      setProjects(transformedProjects);
    } catch (err) {
      console.error("Error fetching client projects:", err);
      setError(err.response?.data?.message || "Failed to load client projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  // Calculate credits info
  const creditsInfo = calculateRemainingCredits(client);

  // Get formatted status
  const displayStatus = getStatusText(client.status);

  return (
    <>
      {/* Overlay */}
      {open && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            zIndex: 1299,
          }}
        />
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: 900, // Increased width to accommodate table
          height: "100vh",
          position: "fixed",
          top: 0,
          right: open ? 0 : "-100%",
          bgcolor: "#fff",
          zIndex: 1300,
          transition: "right 0.3s ease",
          boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
          p: 3,
          overflowY: "auto", // Make sidebar scrollable
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            alignItems: "center",
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
            Client Details
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0.5 }}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Client Info */}
        <Box>
          {/* TOP ROW: Name + Joined Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            {/* Name + Status */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Icon name="profile" color={COLORS.PRIMARY} />
              <Typography sx={TYPOGRAPHY.H3}>{client.name}</Typography>
              <Chip
                label={displayStatus}
                size="small"
                sx={{
                  ...(statusStyles[client.status] || statusStyles.Pending),
                  height: 22,
                  ...TYPOGRAPHY.BODY_SMALL,
                }}
              />
            </Box>

            {/* Joined Date */}
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
              Joined on:{" "}
              {new Date(client.joinedAt || client.createdAt)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })
                .replace(/\//g, "/")}
            </Typography>
          </Box>

          {/* Email + Normal Credits (Same Row) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: "24px",
              mb: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon name="at" size={16} color={COLORS.SECONDARY} />
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}
              >
                {client.email}
              </Typography>
            </Box>

            <Typography
              sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
            >
              Normal Credits: <strong>{creditsInfo.normal.display}</strong>
            </Typography>
          </Box>

          {/* Phone + Outstation Credits (Same Row) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: "24px",
            }}
          >
            {/* Phone */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon name="phoneDone" size={16} color={COLORS.SECONDARY} />
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}
              >
                {client.phone}
              </Typography>
            </Box>

            {/* Outstation Credits (Right Side) */}
            <Typography
              sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
            >
              Outstation Credits:{" "}
              <strong>{creditsInfo.outstation.display}</strong>
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            mt: 2,
            mb: 3,
          }}
        />

        {/* Projects Section Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
            Client Projects ({projects.length})
          </Typography>
          <Typography
            sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY }}
          >
            Showing all projects for this client
          </Typography>
        </Box>

        {/* Projects Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : projects.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              p: 3,
              border: `1px dashed ${COLORS.LIGHT_SECONDARY}`,
              borderRadius: 1,
            }}
          >
            <Typography sx={{ color: COLORS.TEXT }}>
              No projects found for this client
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: COLORS.LIGHT_SECONDARY,
                borderRadius: "4px",
              },
            }}
          >
            <Paper
              elevation={0}
              sx={{ backgroundColor: "transparent", minWidth: "800px" }}
            >
              <TableContainer>
                <Table
                  sx={{
                    backgroundColor: "transparent",
                    "& thead th": {
                      backgroundColor: "transparent",
                      padding: "8px 6px",
                    },
                    "& tbody td": {
                      backgroundColor: "transparent",
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      padding: "8px 6px",
                      verticalAlign: "top",
                    },
                    tableLayout: "fixed",
                    width: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        { key: "Project Name", width: "200px" },
                        { key: "Engineer", width: "120px" },
                        { key: "Site Check", width: "120px" },
                        { key: "Req Date", width: "100px" },
                        { key: "Visit Date", width: "100px" },
                        { key: "Status", width: "100px" },
                      ].map((head) => (
                        <TableCell
                          key={head.key}
                          sx={{
                            ...TYPOGRAPHY.H4,
                            color: COLORS.PRIMARY,
                            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            whiteSpace: "nowrap",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            width: head.width,
                            minWidth: head.width,
                            maxWidth: head.width,
                            padding: "8px 6px",
                          }}
                        >
                          {head.key}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {projects.map((project) => (
                      <TableRow
                        key={project.id}
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        }}
                      >
                        {/* Project Name Column */}
                        <TableCell
                          sx={{
                            width: "200px",
                            minWidth: "200px",
                            maxWidth: "200px",
                            padding: "8px 6px",
                          }}
                        >
                          {/* Report Number Badge - Only show for FINALREPORT status */}
                          {project.status === "FINALREPORT" &&
                          project.projectReportNo &&
                          project.projectReportNo !== "—" ? (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                border: `1.5px solid ${COLORS.PRIMARY}`,
                                backgroundColor: COLORS.SECONDARY,
                                color: COLORS.BACKGROUND,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                marginBottom: "6px",
                                width: "fit-content",
                                minWidth: "60px",
                                textAlign: "center",
                                lineHeight: 1.2,
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {project.projectReportNo}
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_SMALL,
                                color: COLORS.PRIMARY,
                                fontSize: "1rem",
                                marginBottom: "6px",
                                fontWeight: 600,
                              }}
                            >
                              —
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: "break-word",
                              lineHeight: 1.3,
                              mb: 0.5,
                            }}
                          >
                            {project.projectName}
                          </Typography>
                        </TableCell>

                        {/* Engineer Column */}
                        <TableCell
                          sx={{
                            width: "120px",
                            minWidth: "120px",
                            maxWidth: "120px",
                            padding: "8px 6px",
                          }}
                        >
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: "break-word",
                              lineHeight: 1.3,
                              mb: 0.5,
                            }}
                          >
                            {project.engineer}
                          </Typography>
                          {project.engineerContact && (
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_SMALL,
                                color: COLORS.TEXT,
                                fontSize: "0.7rem",
                                wordBreak: "break-word",
                                lineHeight: 1.2,
                              }}
                            >
                              {project.engineerContact}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Site Check Column */}
                        <TableCell
                          sx={{
                            width: "120px",
                            minWidth: "120px",
                            maxWidth: "120px",
                            padding: "8px 6px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: "break-word",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.siteCheck}
                        </TableCell>

                        {/* Request Date Column */}
                        <TableCell
                          sx={{
                            width: "100px",
                            minWidth: "100px",
                            maxWidth: "100px",
                            padding: "8px 6px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: "break-word",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.reqDate}
                        </TableCell>

                        {/* Visit Date Column */}
                        <TableCell
                          sx={{
                            width: "100px",
                            minWidth: "100px",
                            maxWidth: "100px",
                            padding: "8px 6px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: "break-word",
                            lineHeight: 1.3,
                          }}
                        >
                          {project.assignedVisitDate}
                        </TableCell>

                        {/* Status Column */}
                        <TableCell
                          sx={{
                            width: "100px",
                            minWidth: "100px",
                            maxWidth: "100px",
                            padding: "8px 6px",
                          }}
                        >
                          <Chip
                            label={project.status}
                            size="small"
                            sx={{
                              ...(statusStyles[project.status] || {}),
                              height: 20,
                              ...TYPOGRAPHY.BODY_SMALL,
                              fontSize: "0.65rem",
                              textTransform: "capitalize",
                              "& .MuiChip-label": {
                                padding: "0 6px",
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>
    </>
  );
};

export default ClientDetailsSidebar;
